import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment, Stack, Chip, Tooltip,
  ToggleButtonGroup, ToggleButton, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  CircularProgress, Alert, MenuItem, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, IconButton, Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Hub as HubIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  dtoolsCrmStats,
  dtoolsCrmStages,
  dtoolsCrmPipeline,
  dtoolsCrmClients,
  dtoolsCrmClient,
  dtoolsCrmContacts,
} from '../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtInt   = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const fmtMoney = (n) => {
  if (n == null || n === '') return '—';
  return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
};
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};
const fmtAddress = (a) => {
  if (!a) return '—';
  const parts = [a.Street1, a.City, a.State, a.PostalCode].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const CATEGORY_COLORS = {
  open:    'primary',
  won:     'success',
  lost:    'error',
  hold:    'warning',
  archive: 'default',
};

function compareValues(a, b, dir) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return dir === 'asc' ? a - b : b - a;
  const s1 = String(a).toLowerCase();
  const s2 = String(b).toLowerCase();
  if (s1 < s2) return dir === 'asc' ? -1 : 1;
  if (s1 > s2) return dir === 'asc' ? 1  : -1;
  return 0;
}

// ─── Pipeline Tab ───────────────────────────────────────────────────────────
function PipelineTab({ search }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [category, setCategory] = useState('open');
  const [salesRep, setSalesRep] = useState('');
  const [sortBy, setSortBy]   = useState('stage_order');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {};
    if (category)  params.category  = category;
    if (salesRep)  params.sales_rep = salesRep;
    if (search && search.trim().length >= 2) params.search = search.trim();
    dtoolsCrmPipeline(params)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [category, salesRep, search]);

  const salesReps = useMemo(() => {
    if (!data?.projects) return [];
    return [...new Set(data.projects.map((p) => p.sales_rep).filter(Boolean))].sort();
  }, [data]);

  const handleSort = (key) => {
    setSortBy((prev) => {
      if (prev === key) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return prev; }
      setSortDir(['price','cost','margin','hours','stage_order'].includes(key) ? 'asc' : 'asc');
      return key;
    });
  };

  const sorted = useMemo(() => {
    if (!data?.projects) return [];
    return [...data.projects].sort((a, b) => compareValues(a[sortBy], b[sortBy], sortDir));
  }, [data, sortBy, sortDir]);

  const totalPrice = useMemo(() => sorted.reduce((s, p) => s + (Number(p.price) || 0), 0), [sorted]);

  const cols = [
    { key: 'job_number',      label: 'Job #',      align: 'left'  },
    { key: 'project_name',    label: 'Project',    align: 'left'  },
    { key: 'client_name',     label: 'Client',     align: 'left'  },
    { key: 'stage_label',     label: 'Stage',      align: 'left'  },
    { key: 'sales_rep',       label: 'Sales',      align: 'left'  },
    { key: 'project_manager', label: 'PM',         align: 'left'  },
    { key: 'price',           label: 'Price',      align: 'right' },
    { key: 'estimated_close_date', label: 'Est. Close', align: 'left' },
  ];

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={category}
          onChange={(_, v) => v != null && setCategory(v)}
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="open">Open</ToggleButton>
          <ToggleButton value="hold">On Hold</ToggleButton>
          <ToggleButton value="won">Won</ToggleButton>
          <ToggleButton value="lost">Lost</ToggleButton>
          <ToggleButton value="archive">Archive</ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Sales Rep</InputLabel>
          <Select
            label="Sales Rep"
            value={salesRep}
            onChange={(e) => setSalesRep(e.target.value)}
          >
            <MenuItem value="">All reps</MenuItem>
            {salesReps.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <Paper variant="outlined">
          <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="body2"><strong>{fmtInt(sorted.length)}</strong> projects</Typography>
              <Typography variant="body2">Pipeline value:&nbsp;<strong>{fmtMoney(totalPrice)}</strong></Typography>
            </Stack>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.8rem', bgcolor: '#f3f4f6' } }}>
                  {cols.map((c) => (
                    <TableCell key={c.key} align={c.align} sortDirection={sortBy === c.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortBy === c.key}
                        direction={sortBy === c.key ? sortDir : 'asc'}
                        onClick={() => handleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((p) => (
                  <TableRow key={p.project_id} hover>
                    <TableCell>
                      <Link component={RouterLink} to={`/projects/${p.project_id}`}
                            underline="hover" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {p.job_number || p.dtools_number || `#${p.project_id}`}
                      </Link>
                    </TableCell>
                    <TableCell>{p.project_name || '—'}</TableCell>
                    <TableCell>{p.client_name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.stage_label || p.progress || '—'}
                        color={CATEGORY_COLORS[p.stage_category] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{p.sales_rep || '—'}</TableCell>
                    <TableCell>{p.project_manager || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtMoney(p.price)}</TableCell>
                    <TableCell>{fmtDate(p.estimated_close_date)}</TableCell>
                  </TableRow>
                ))}
                {sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={cols.length} sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                      No projects match these filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

// ─── Client Detail Dialog ───────────────────────────────────────────────────
function ClientDetailDialog({ clientId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    dtoolsCrmClient(clientId)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [clientId]);

  return (
    <Dialog open={!!clientId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {data?.client?.name || 'Client'}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {data && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {data.client.client_number && <Chip size="small" label={`AR: ${data.client.client_number}`} />}
              <Chip size="small" label={`${data.client.project_count} projects`} />
              <Chip size="small" color="success" label={`${data.client.approved_count} approved`} />
              <Chip size="small" label={`Total: ${fmtMoney(data.client.total_price)}`} />
            </Stack>

            <Box>
              <Typography variant="overline" color="text.secondary">Contacts ({data.contacts.length})</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f3f4f6' } }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Mobile</TableCell>
                      <TableCell align="right">Projects</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.contacts.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell sx={{ fontWeight: c.is_primary ? 700 : 400 }}>
                          {c.name} {c.is_primary && <Chip size="small" label="Primary" color="primary" sx={{ ml: 1, height: 18 }} />}
                        </TableCell>
                        <TableCell>{c.title || '—'}</TableCell>
                        <TableCell>{c.email ? <Link href={`mailto:${c.email}`}>{c.email}</Link> : '—'}</TableCell>
                        <TableCell>{c.phone || '—'}</TableCell>
                        <TableCell>{c.mobile || '—'}</TableCell>
                        <TableCell align="right">{c.project_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">Projects ({data.projects.length})</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f3f4f6' } }}>
                      <TableCell>Job #</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Stage</TableCell>
                      <TableCell>Sales</TableCell>
                      <TableCell>PM</TableCell>
                      <TableCell align="right">Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.projects.map((p) => (
                      <TableRow key={p.project_id} hover>
                        <TableCell>
                          <Link component={RouterLink} to={`/projects/${p.project_id}`} sx={{ fontFamily: 'monospace' }}>
                            {p.job_number || p.dtools_number}
                          </Link>
                        </TableCell>
                        <TableCell>{p.project_name}</TableCell>
                        <TableCell>
                          <Chip size="small" label={p.stage_label || p.progress || '—'}
                                color={CATEGORY_COLORS[p.stage_category] || 'default'} variant="outlined" />
                        </TableCell>
                        <TableCell>{p.sales_rep || '—'}</TableCell>
                        <TableCell>{p.project_manager || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmtMoney(p.price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Clients Tab ────────────────────────────────────────────────────────────
function ClientsTab({ search }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openClient, setOpenClient] = useState(null);
  const [sortBy, setSortBy]   = useState('total_price');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {};
    if (search && search.trim().length >= 2) params.search = search.trim();
    dtoolsCrmClients(params)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [search]);

  const handleSort = (key) => {
    setSortBy((prev) => {
      if (prev === key) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return prev; }
      setSortDir(['total_price','project_count','approved_count','contact_count'].includes(key) ? 'desc' : 'asc');
      return key;
    });
  };

  const sorted = useMemo(() => {
    if (!data?.clients) return [];
    return [...data.clients].sort((a, b) => compareValues(a[sortBy], b[sortBy], sortDir));
  }, [data, sortBy, sortDir]);

  const cols = [
    { key: 'name',           label: 'Client',          align: 'left'  },
    { key: 'client_number',  label: 'AR Code',         align: 'left'  },
    { key: 'project_count',  label: 'Projects',        align: 'right' },
    { key: 'approved_count', label: 'Approved',        align: 'right' },
    { key: 'contact_count',  label: 'Contacts',        align: 'right' },
    { key: 'total_price',    label: 'Total Value',     align: 'right' },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <Paper variant="outlined">
          <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="body2"><strong>{fmtInt(sorted.length)}</strong> clients</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.8rem', bgcolor: '#f3f4f6' } }}>
                  {cols.map((c) => (
                    <TableCell key={c.key} align={c.align} sortDirection={sortBy === c.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortBy === c.key}
                        direction={sortBy === c.key ? sortDir : 'asc'}
                        onClick={() => handleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((c) => (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenClient(c.id)}>
                    <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {c.client_number || '—'}
                    </TableCell>
                    <TableCell align="right">{fmtInt(c.project_count)}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{fmtInt(c.approved_count)}</TableCell>
                    <TableCell align="right">{fmtInt(c.contact_count)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtMoney(c.total_price)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {openClient && <ClientDetailDialog clientId={openClient} onClose={() => setOpenClient(null)} />}
    </Box>
  );
}

// ─── Contacts Tab ───────────────────────────────────────────────────────────
function ContactsTab({ search }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasEmail, setHasEmail] = useState(false);
  const [sortBy, setSortBy]   = useState('project_count');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {};
    if (search && search.trim().length >= 2) params.search = search.trim();
    if (hasEmail) params.hasEmail = 'true';
    dtoolsCrmContacts(params)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [search, hasEmail]);

  const handleSort = (key) => {
    setSortBy((prev) => {
      if (prev === key) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return prev; }
      setSortDir(['project_count'].includes(key) ? 'desc' : 'asc');
      return key;
    });
  };

  const sorted = useMemo(() => {
    if (!data?.contacts) return [];
    return [...data.contacts].sort((a, b) => compareValues(a[sortBy], b[sortBy], sortDir));
  }, [data, sortBy, sortDir]);

  const cols = [
    { key: 'name',          label: 'Name',     align: 'left'  },
    { key: 'title',         label: 'Title',    align: 'left'  },
    { key: 'client_name',   label: 'Client',   align: 'left'  },
    { key: 'email',         label: 'Email',    align: 'left'  },
    { key: 'phone',         label: 'Phone',    align: 'left'  },
    { key: 'mobile',        label: 'Mobile',   align: 'left'  },
    { key: 'project_count', label: 'Projects', align: 'right' },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={hasEmail ? 'yes' : 'no'}
          onChange={(_, v) => v && setHasEmail(v === 'yes')}
        >
          <ToggleButton value="no">All contacts</ToggleButton>
          <ToggleButton value="yes">With email only</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <Paper variant="outlined">
          <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="body2">
              <strong>{fmtInt(sorted.length)}</strong> contact{sorted.length === 1 ? '' : 's'}
              {sorted.length === 1000 && ' (capped at 1,000)'}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.8rem', bgcolor: '#f3f4f6' } }}>
                  {cols.map((c) => (
                    <TableCell key={c.key} align={c.align} sortDirection={sortBy === c.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortBy === c.key}
                        direction={sortBy === c.key ? sortDir : 'asc'}
                        onClick={() => handleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontWeight: c.is_primary ? 700 : 400 }}>
                      {c.name}
                      {c.is_primary && <Chip size="small" label="Primary" color="primary" sx={{ ml: 1, height: 18 }} />}
                    </TableCell>
                    <TableCell>{c.title || '—'}</TableCell>
                    <TableCell>{c.client_name || '—'}</TableCell>
                    <TableCell>{c.email ? <Link href={`mailto:${c.email}`}>{c.email}</Link> : '—'}</TableCell>
                    <TableCell>{c.phone || '—'}</TableCell>
                    <TableCell>{c.mobile || '—'}</TableCell>
                    <TableCell align="right">{fmtInt(c.project_count)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────
export default function DtoolsCrm() {
  const [stats, setStats]   = useState(null);
  const [tab, setTab]       = useState('pipeline');
  const [search, setSearch] = useState('');

  useEffect(() => { dtoolsCrmStats().then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <HubIcon color="primary" />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>CRM (D-Tools)</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sales pipeline, clients, and contacts — sourced from D-Tools. Replaces Zoho CRM.
      </Typography>

      {/* Stats strip */}
      {stats && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
          <Chip size="small" variant="outlined" label={`${fmtInt(stats.clients)} clients`} />
          <Chip size="small" variant="outlined" label={`${fmtInt(stats.contacts)} contacts`} />
          <Chip size="small" variant="outlined" label={`${fmtInt(stats.projects)} projects`} />
          <Chip size="small" color="primary" variant="outlined" label={`${fmtInt(stats.open_projects)} open`} />
          <Chip size="small" color="success" variant="outlined" label={`${fmtInt(stats.won_projects)} won`} />
          <Chip size="small" color="error"   variant="outlined" label={`${fmtInt(stats.lost_projects)} lost`} />
          {stats.last_synced && (
            <Chip size="small" variant="outlined" label={`Last synced: ${fmtDate(stats.last_synced)}`} />
          )}
        </Stack>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <TextField
          fullWidth
          size="small"
          placeholder="Search across pipeline, clients, contacts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="pipeline" label="Pipeline" />
        <Tab value="clients"  label="Clients" />
        <Tab value="contacts" label="Contacts" />
      </Tabs>

      {tab === 'pipeline' && <PipelineTab search={search} />}
      {tab === 'clients'  && <ClientsTab  search={search} />}
      {tab === 'contacts' && <ContactsTab search={search} />}
    </Box>
  );
}
